import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import s from './SmileGallery.module.css';

// Each entry has ONE composite image where:
//   left half  = Before treatment
//   right half = After treatment
// The slider reveals each half by using object-position on the img tags.
const SERVICES = [
  {
    id: 'veneers',
    index: '01',
    title: 'Veneers',
    badge: 'Most Popular',
    color: '#0e8585',
    description:
      'Ultra-thin porcelain shells crafted to cover chips, stains, gaps and misalignment — delivering a perfectly proportioned, radiant smile in as little as two visits.',
    facts: ['Completed in 2-3 visits', 'Stain-resistant porcelain', 'Natural-looking results'],
    before: '/images/smilegallery/veneers-before.jpg',
    after:  '/images/smilegallery/veneers-after.jpg',
    //note: 'Case: 6 upper porcelain veneers, 2 appointments',
  },
  {
    id: 'crowns',
    index: '02',
    title: 'Porcelain Crowns',
    badge: 'Restorative',
    color: '#1a4a6e',
    description:
      'Full-coverage ceramic caps that restore a cracked or severely discoloured tooth to its natural shape, colour and full biting strength — built to last for decades.',
    facts: ['Same-day CEREC crowns available', '100% metal-free ceramic', 'Matched to your natural teeth'],
    before: '/images/smilegallery/crowns-before.jpg',
    after:  '/images/smilegallery/crowns-after.jpg',
    //note: 'Case: 4 ceramic posterior crowns, CEREC same-day procedure',
  },
  {
    id: 'bonding',
    index: '03',
    title: 'Dental Bonding',
    badge: 'Same-Day',
    color: '#0b5c5c',
    description:
      'Tooth-coloured composite resin applied and sculpted directly in one visit — repairing chips, cracks, gaps and discolouration with zero drilling and no anaesthesia needed.',
    facts: ['Completed in one visit (~90 min)', 'No drilling or anaesthesia', 'Immediately visible results'],
    before: '/images/smilegallery/bonding-before.jpg',
    after:  '/images/smilegallery/bonding-after.jpg',
    //note: 'Case: Chipped and discoloured front teeth, single visit',
  },
];

// ─── Before/After Slider ────────────────────────────────────────────────────
// Uses a single composite image where left half = Before, right half = After.
// The "before" img is anchored left (object-position: left center) and the
// "after" img is anchored right (object-position: right center), each
// covering the full stage. The clip div reveals only the Before portion.
function BeforeAfterSlider({ before, after, note, color }) {
  const [pct, setPct] = useState(50);
  const [active, setActive] = useState(false);
  const [hinted, setHinted] = useState(false);
  const wrapRef = useRef(null);
  const [stageW, setStageW] = useState(0);

  const move = useCallback((clientX) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setPct(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
    setHinted(true);
  }, []);

  const onMouseDown  = (e) => { e.preventDefault(); setActive(true); move(e.clientX); };
  const onTouchStart = (e) => { setActive(true); move(e.touches[0].clientX); };
  const onTouchEnd   = ()  => setActive(false);
  const onTouchMove  = useCallback((e) => { if (active) move(e.touches[0].clientX); }, [active, move]);
  const onMouseMove  = useCallback((e) => { if (active) move(e.clientX); }, [active, move]);
  const onMouseUp    = useCallback(() => setActive(false), []);
  const onKeyDown    = (e) => {
    if (e.key === 'ArrowLeft')  setPct((p) => Math.max(2,  p - 2));
    if (e.key === 'ArrowRight') setPct((p) => Math.min(98, p + 2));
    setHinted(true);
  };

  useEffect(() => {
    if (!active) return;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, [active, onMouseMove, onMouseUp]);

  // Track stage width so imgBefore can be exactly stage-wide
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([e]) => setStageW(e.contentRect.width));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={s.sliderRoot}>
      <div
        ref={wrapRef}
        className={s.stage}
        style={{ cursor: active ? 'col-resize' : 'ew-resize', '--stage-w': stageW + 'px' }}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="slider"
        aria-valuenow={Math.round(pct)}
        aria-label="Drag to compare before and after"
      >
        {/*
          TWO-IMAGE SPLIT SLIDER
          ───────────────────────────────────────────────────────────
          At rest (pct = 50): before fills left 50%, after fills right 50%.
          Together they look like one seamless image.

          AFTER image:  fills full stage, object-position: left center
            → left edge of "after" always sits at left of stage.
            → at pct=50 only the left half is visible (right half hidden behind before clip).
            → drag right → clip grows → more after exposed → full after at pct=98.

          BEFORE clip:  width = pct%. The img inside is stage-width wide
            (width set to 100vw so it isn't constrained by the narrow clip div).
            object-position: right center
            → right edge of "before" always sits at right edge of the img (= scrub line).
            → at pct=50 only the right half of before is visible inside the clip.
            → drag left → clip shrinks → less before → full before at pct=2.

          Net result: at pct=50 the two images butt up perfectly in the center.
          Dragging either way reveals the full image on that side.
          ─────────────────────────────────────────────────────────── */}

        {/* AFTER: full stage, anchored left so left edge stays at stage-left */}
        <img
          src={after}
          alt="After treatment"
          className={s.imgAfter}
          draggable={false}
        />

        {/* BEFORE: clipped to pct% wide; img is stage-width, anchored right
            so its right edge always aligns with the scrub line */}
        <div className={s.beforeClip} style={{ width: `${pct}%` }}>
          <img
            src={before}
            alt="Before treatment"
            className={s.imgBefore}
            draggable={false}
          />
        </div>

        {/* Scrub line + handle */}
        <div className={s.scrubLine} style={{ left: `${pct}%` }}>
          <div className={s.scrubHandle} style={{ background: color }}>
            <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 15l-5-5 5-5"/>
            </svg>
            <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
              <path d="M8 15l5-5-5-5"/>
            </svg>
          </div>
        </div>

        {/* Labels */}
        <span className={s.tagLeft}>Before</span>
        <span className={s.tagRight}>After</span>

        {/* Drag hint */}
        <div className={`${s.hint}${hinted ? ' ' + s.hintGone : ''}`}>
          <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 15l-5-5 5-5"/>
          </svg>
          Drag to compare
          <svg viewBox="0 0 20 20" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 15l5-5-5-5"/>
          </svg>
        </div>
      </div>

      {note && (
        <div className={s.noteBar}>
          <span className={s.noteDot} style={{ background: color }} />
          {note}
        </div>
      )}
    </div>
  );
}


// ─── Service card ────────────────────────────────────────────────────────────
function ServiceCard({ svc, flip }) {
  return (
    <div className={`${s.card}${flip ? ' ' + s.cardFlip : ''}`}>
      <div className={s.textCol}>
        <span className={s.indexNum}>{svc.index}</span>
        <span className={s.badge} style={{ background: svc.color }}>{svc.badge}</span>
        <h2 className={s.cardTitle}>{svc.title}</h2>
        <p className={s.cardDesc}>{svc.description}</p>
        <ul className={s.facts}>
          {svc.facts.map((f) => (
            <li key={f} className={s.factItem}>
              <span className={s.factCheck} style={{ background: svc.color }}>
                <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                  <path d="M1.5 5l2.5 2.5 5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {f}
            </li>
          ))}
        </ul>
       {/* <Link to="/services/cosmetic-dentistry" className={s.cardLink} style={{ '--lc': svc.color }}>
          Learn about {svc.title}
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 10h12M10 4l6 6-6 6"/>
          </svg>
        </Link> */}
      </div>
      <div className={s.sliderCol}>
        <BeforeAfterSlider before={svc.before} after={svc.after} note={svc.note} color={svc.color} />
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function SmileGallery() {
  return (
    <div className={s.root}>
      <div className={s.intro}>
        <p className={s.eyebrow}>Real Patients &middot; Verified Results</p>
        <h1 className={s.heroTitle}>
          Before &amp; After<br/>
          <em>Smile Transformations</em>
        </h1>
        <p className={s.heroSub}>
          Drag the slider on each photo to reveal the full transformation.
          All results are from real Celebrity Smile Studio patients.
        </p>
        <div className={s.trustRow}>
          {[
            { n: '4.9★', l: 'Google Rating' },
            { n: '900+', l: '5-Star Reviews' },
            { n: '20+',  l: 'Yrs Experience' },
          ].map((t, i, arr) => (
            <React.Fragment key={t.l}>
              <div className={s.trustStat}>
                <span className={s.trustN}>{t.n}</span>
                <span className={s.trustL}>{t.l}</span>
              </div>
              {i < arr.length - 1 && <div className={s.trustDiv}/>}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className={s.cards}>
        {SERVICES.map((svc, i) => (
          <ServiceCard key={svc.id} svc={svc} flip={i % 2 === 1} />
        ))}
      </div>

      <div className={s.ctaBand}>
        <div className={s.ctaText}>
          <h3 className={s.ctaTitle}>Ready to Transform Your Smile?</h3>
          <p className={s.ctaSub}>Book a free consultation and see what is possible for you.</p>
        </div>
        <div className={s.ctaActions}>
          <Link to="/book-appointment" className="btn btn-primary">Book Free Consultation</Link>
          <a href="tel:2152335811" className={s.ctaPhone}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16z"/>
            </svg>
            (215) 233-5811
          </a>
        </div>
      </div>

      <p className={s.disclaimer}>
        * All photographs are used with written patient consent. Individual outcomes vary based on dental history and anatomy.
        A consultation is required to determine candidacy for any cosmetic procedure.
      </p>
    </div>
  );
}
